import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SyncIcon from '@mui/icons-material/Sync';
import VMDetails from './VMDetails';
import ConfirmDialog from './ConfirmDialog';

interface VM {
  vmid: number;
  name: string;
  status: string;
  node: string;
  cpu: number;
  cpus: number;
  mem: number;
  maxmem: number;
  disk: number;
  maxdisk: number;
  uptime: number;
  type: string;
}

interface ResourcesProps {
  token: string;
  userRole?: 'admin' | 'tenant';
}

const Resources: React.FC<ResourcesProps> = ({ token, userRole }) => {
  const [vms, setVms] = useState<VM[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [selectedVM, setSelectedVM] = useState<VM | null>(null);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
    severity: 'warning' | 'error' | 'info';
  }>({ open: false, title: '', message: '', action: () => {}, severity: 'warning' });

  const fetchVMs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/proxmox/vms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch VMs');
      const data = await res.json();
      setVms(data);
    } catch (err: any) {
      setError(err.message || 'Error loading VMs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVMs();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchVMs, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const handleVMAction = async (node: string, vmid: number, action: string, vmType: string, event: React.MouseEvent, vmName?: string) => {
    event.stopPropagation(); // Prevent row click
    const key = `${vmid}-${action}`;
    
    if (actionLoading[key]) return; // Prevent multiple clicks
    
    // Show confirmation for destructive actions
    const actionMessages: Record<string, { message: string; severity: 'warning' | 'error' | 'info' }> = {
      stop: { 
        message: `Force stopping VM "${vmName || vmid}" will immediately terminate all processes without graceful shutdown.`, 
        severity: 'error' 
      },
      shutdown: { 
        message: `VM "${vmName || vmid}" will be gracefully shut down.`, 
        severity: 'warning' 
      },
      reboot: { 
        message: `VM "${vmName || vmid}" will be rebooted. This will temporarily interrupt services.`, 
        severity: 'warning' 
      }
    };
    
    if (actionMessages[action]) {
      setConfirmDialog({
        open: true,
        title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
        message: actionMessages[action].message,
        severity: actionMessages[action].severity,
        action: () => executeVMAction(node, vmid, action, vmType, key)
      });
      return;
    }
    
    // For non-destructive actions (like start), execute directly
    executeVMAction(node, vmid, action, vmType, key);
  };
  
  const executeVMAction = async (node: string, vmid: number, action: string, vmType: string, key: string) => {
    setActionLoading({ ...actionLoading, [key]: true });
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/proxmox/vms/${node}/${vmid}/${action}?vm_type=${vmType}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to ${action} VM`);
      }
      
      const result = await res.json();
      const taskId = result.task; // UPID from Proxmox
      
      // Poll for task completion
      if (taskId) {
        await pollTaskStatus(node, taskId, key);
      } else {
        // No task ID, just refresh after delay
        setTimeout(async () => {
          await fetchVMs();
          setActionLoading({ ...actionLoading, [key]: false });
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || `Error ${action}ing VM`);
      setActionLoading({ ...actionLoading, [key]: false }); // Re-enable only on error
    }
  };

  const pollTaskStatus = async (node: string, upid: string, actionKey: string) => {
    const maxAttempts = 30; // Max 30 seconds
    let attempts = 0;
    
    const poll = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/proxmox/tasks/${node}/${encodeURIComponent(upid)}/status`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (res.ok) {
          const status = await res.json();
          
          if (status.status === 'stopped') {
            // Task completed
            await fetchVMs();
            setActionLoading(prev => ({ ...prev, [actionKey]: false }));
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000); // Poll every second
        } else {
          // Timeout, refresh anyway
          await fetchVMs();
          setActionLoading(prev => ({ ...prev, [actionKey]: false }));
        }
      } catch (err) {
        // On error, just refresh and re-enable
        await fetchVMs();
        setActionLoading(prev => ({ ...prev, [actionKey]: false }));
      }
    };
    
    poll();
  };

  const handleRowClick = (vm: VM) => {
    setSelectedVM(vm);
  };

  const handleBackToList = () => {
    setSelectedVM(null);
    fetchVMs(); // Refresh data when coming back
  };

  // If a VM is selected, show the details view
  if (selectedVM) {
    return <VMDetails vm={selectedVM} token={token} onBack={handleBackToList} />;
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 250 },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const vm = params.row as VM;
        const isLoading = Object.keys(actionLoading).some(key => key.startsWith(`${vm.vmid}-`) && actionLoading[key]);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={params.value}
              size="small"
              color={params.value === 'running' ? 'success' : 'default'}
            />
            {isLoading && (
              <Tooltip title="Operation in progress...">
                <SyncIcon 
                  sx={{ 
                    fontSize: 18, 
                    color: 'primary.main',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }} 
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    { field: 'node', headerName: 'Node', width: 120 },
    {
      field: 'cpu',
      headerName: 'CPU Usage',
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const cpuPercent = params.row.cpus > 0 ? ((params.value / params.row.cpus) * 100).toFixed(1) : '0.0';
        return `${cpuPercent}% (${params.row.cpus} cores)`;
      },
    },
    {
      field: 'mem',
      headerName: 'Memory',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const memPercent = params.row.maxmem > 0 ? ((params.value / params.row.maxmem) * 100).toFixed(1) : '0';
        return `${formatBytes(params.value)} / ${formatBytes(params.row.maxmem)} (${memPercent}%)`;
      },
    },
    {
      field: 'disk',
      headerName: 'Disk',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        return `${formatBytes(params.value)} / ${formatBytes(params.row.maxdisk)}`;
      },
    },
    ...(userRole === 'admin' ? [{
      field: 'uptime',
      headerName: 'Uptime',
      width: 100,
      renderCell: (params: GridRenderCellParams) => formatUptime(params.value),
    }] : []),
    {
      field: 'actions',
      headerName: 'Quick Actions',
      width: 180,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const vm = params.row as VM;
        const { node, vmid, status, type } = vm;
        const isRunning = status === 'running';
        const isStopped = status === 'stopped';
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isStopped && (
              <Tooltip title="Start">
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => handleVMAction(node, vmid, 'start', type, e, vm.name)}
                  disabled={actionLoading[`${vmid}-start`]}
                >
                  {actionLoading[`${vmid}-start`] ? (
                    <SyncIcon 
                      fontSize="small" 
                      sx={{ 
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }} 
                    />
                  ) : (
                    <PlayArrowIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {isRunning && (
              <>
                <Tooltip title="Shutdown">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={(e) => handleVMAction(node, vmid, 'shutdown', type, e, vm.name)}
                    disabled={actionLoading[`${vmid}-shutdown`]}
                  >
                    {actionLoading[`${vmid}-shutdown`] ? (
                      <SyncIcon 
                        fontSize="small" 
                        sx={{ 
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }} 
                      />
                    ) : (
                      <PowerSettingsNewIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Force Stop">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleVMAction(node, vmid, 'stop', type, e, vm.name)}
                    disabled={actionLoading[`${vmid}-stop`]}
                  >
                    {actionLoading[`${vmid}-stop`] ? (
                      <SyncIcon 
                        fontSize="small" 
                        sx={{ 
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }} 
                      />
                    ) : (
                      <StopIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reboot">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => handleVMAction(node, vmid, 'reboot', type, e, vm.name)}
                    disabled={actionLoading[`${vmid}-reboot`]}
                  >
                    {actionLoading[`${vmid}-reboot`] ? (
                      <SyncIcon 
                        fontSize="small" 
                        sx={{ 
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }} 
                      />
                    ) : (
                      <RestartAltIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Resources
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchVMs}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Click on any VM row to view details, manage snapshots, backups, firewall, and activity logs
      </Alert>

      <Paper sx={{ height: 600, width: '100%' }}>
        {loading && vms.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={vms}
            columns={columns}
            getRowId={(row) => row.vmid}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            onRowClick={(params) => handleRowClick(params.row as VM)}
            sx={{
              '& .MuiDataGrid-cell': {
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          />
        )}
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`Total VMs: ${vms.length}`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`Running: ${vms.filter(vm => vm.status === 'running').length}`}
          color="success"
          variant="outlined"
        />
        <Chip
          label={`Stopped: ${vms.filter(vm => vm.status === 'stopped').length}`}
          color="default"
          variant="outlined"
        />
      </Box>
      
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        onConfirm={() => {
          confirmDialog.action();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        confirmText="Proceed"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default Resources;
